import firebase from 'firebase/compat/app';
import { db } from './config';

export type Discipline = 'boulder' | 'lead' | 'speed';

export interface TimetableRow {
  start: string;
  end: string;
  activity: string;
  highlight?: boolean;
}

export interface CompetitionMetadata {
  id: string;
  name: string;
  date: string;          // ISO date string, e.g. "2026-06-28"
  location: string;
  disciplines: Discipline[];
  adminSecret: string;
  status: 'planning' | 'live' | 'finished';
  inBelgium?: boolean;
  timetable?: TimetableRow[];
  createdAt?: firebase.firestore.Timestamp;
}

const COLLECTION = 'competitions';

/** Create a new competition document. The caller supplies the ID (slug). */
export async function createCompetition(
  data: Omit<CompetitionMetadata, 'createdAt'>,
): Promise<void> {
  await db.collection(COLLECTION).doc(data.id).set({
    ...data,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

/** One-time read. Returns null if not found. */
export async function getCompetition(id: string): Promise<CompetitionMetadata | null> {
  const snap = await db.collection(COLLECTION).doc(id).get();
  if (!snap.exists) return null;
  return { id: snap.id, ...(snap.data() as object) } as CompetitionMetadata;
}

/** Real-time listener. Returns unsubscribe fn. */
export function subscribeCompetition(
  id: string,
  onData: (data: CompetitionMetadata | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return db.collection(COLLECTION).doc(id).onSnapshot(
    snap => onData(snap.exists ? ({ id: snap.id, ...(snap.data() as object) } as CompetitionMetadata) : null),
    err => { console.error('Competition listener error:', err); onError?.(err); },
  );
}

/** Partial update — e.g. save timetable without overwriting other fields. */
export async function updateCompetition(
  id: string,
  patch: Partial<Omit<CompetitionMetadata, 'id' | 'createdAt'>>,
): Promise<void> {
  await db.collection(COLLECTION).doc(id).update(patch);
}

/** List all competitions, sorted by date descending. */
export async function listCompetitions(): Promise<CompetitionMetadata[]> {
  const snap = await db.collection(COLLECTION).orderBy('date', 'desc').get();
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as object) } as CompetitionMetadata));
}
